/**
 * Phase Zero provider-source foundation.
 *
 * Meta stays a read-only evidence source. This screen starts a durable
 * bounded-page background sync, follows its compact task projection, lists
 * server-owned source heads that point to immutable snapshots, and creates a
 * separate strict Segmently copy. It never claims a rendition is the original
 * upload master or mutates Meta. Language and taxonomy AI run only after an
 * explicit, separately estimated confirmation.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LanguageIcon from '@mui/icons-material/Language';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { StyledTextField } from '@modules/ui-components';
import { useProjectLocales } from '@modules/locale-config';
import { useTask, useTaskTransition } from '@modules/background-tasks';
import type { BackendTask } from '@modules/background-tasks';
import { useBackendTask } from '@modules/content-agent/hooks/useBackendTask';
import { AD_CREATIVE_PROVIDER_SOURCES as PROVIDER_SOURCES } from '../../selectors';
import type {
  AdCreativeApiClient,
  AdCreativeProviderBaselineChoice,
  AdCreativeProviderSourceCandidate,
  AdCreativeProviderSourceDetail,
  AdCreativeProviderSourceHead,
  AdCreativeProviderSourceLanguageDetectionPreflight,
  AdCreativeProviderSourceLanguageDetectionAttention,
  AdCreativeProviderSourceLanguageDetectionTaskStatus,
  AdCreativeProviderSourceLanguageProjection,
  AdCreativeProviderSourceSyncPreflight,
  AdCreativeProviderSourceSyncProgress,
  AdCreativeProviderSourceTaxonomyCurrent,
  AdCreativeProviderSourceTaxonomyFields,
  AdCreativeProviderSourceTaxonomyPreflight,
  MetaAdsCampaignSummary,
} from '../../services/AdCreativeApiClient';
import { ProviderSourceMediaGallery } from './ProviderSourceMediaGallery';

export { PROVIDER_SOURCES as IMPORT_REFRESH };

interface MetaImportRefreshPageProps {
  projectId: string;
  client: AdCreativeApiClient;
  onBack: () => void;
  onOpenIntegrations?: () => void;
  /** Parent-route callback is intentionally unused because source drafts do not have a brief yet. */
  onOpenBatch?: (briefId: string) => void;
}

export type ProviderSourceLanguageUiState = 'known' | 'suggested' | 'unknown';

const TERMINAL_LANGUAGE_DETECTION_STATUSES = new Set<AdCreativeProviderSourceLanguageDetectionTaskStatus>([
  'completed',
  'error',
  'cancelled',
  'skipped',
]);

export function providerSourceLanguageUiState(
  language: AdCreativeProviderSourceLanguageProjection,
): ProviderSourceLanguageUiState {
  if (language.mixedLanguage || !language.locale || language.origin === 'unknown') return 'unknown';
  if (language.origin === 'ai_detected' || language.origin === 'heuristic_detected') return 'suggested';
  return 'known';
}

function taxonomyStateLabel(state: AdCreativeProviderSourceHead['taxonomyState']): string {
  switch (state) {
    case 'suggested': return 'AI suggestion · review required';
    case 'reviewed': return 'Taxonomy reviewed';
    case 'rejected': return 'Taxonomy rejected';
    case 'stale': return 'Taxonomy stale · re-analyze';
    default: return 'Taxonomy not analyzed';
  }
}

export function isExactSnapshotLanguageConfirmed(
  detail: AdCreativeProviderSourceDetail,
  locale: string,
  sourceBindingId: string,
  providerBaselineEvidenceId?: string,
): boolean {
  return detail.languageEvidence.some((evidence) => (
    evidence.sourceSnapshotId === detail.currentSnapshot.id
    && evidence.sourceBindingId === sourceBindingId
    && evidence.providerBaselineEvidenceId === providerBaselineEvidenceId
    && evidence.origin === 'user_confirmed'
    && evidence.locale === locale
    && !evidence.mixedLanguage
  ));
}

function languageDetectionAttentionMessage(
  attention: AdCreativeProviderSourceLanguageDetectionAttention,
): string {
  if (attention.kind === 'operator_reconciliation_required') {
    return 'The previous provider outcome is unknown, so automatic replay is blocked. Choose and confirm a project language manually below, or ask an operator to reconcile the saved task. Segmently will not call the provider again automatically.';
  }
  if (attention.kind === 'repair_required') {
    return attention.code === 'source_language_detection_suggestion_corrupt'
      ? 'The detector finished, but its saved suggestion projection failed an integrity check. Restore the suggestion from the settled result; this does not call the provider or bill again.'
      : 'The detector finished, but its saved suggestion projection is missing. Restore the suggestion from the settled result; this does not call the provider or bill again.';
  }

  switch (attention.billingBlock?.type) {
    case 'missing_subscription':
      return 'AI language detection needs an active subscription. You can still choose and confirm the project language manually below.';
    case 'missing_ai_tokens':
    case 'insufficient_credits':
      return 'AI language detection needs more AI credits. You can still choose and confirm the project language manually below.';
    case 'quota_exhausted':
      return 'The AI usage limit has been reached. You can still choose and confirm the project language manually below.';
    case 'missing_model_price':
    case 'billing_config_error':
      return 'AI language detection is unavailable until its billing configuration is reconciled. You can still choose and confirm the project language manually below.';
    default:
      return 'AI language detection needs a billing action. You can still choose and confirm the project language manually below.';
  }
}

export function defaultProviderSourceAssociationWindow(now = new Date()): { from: string; to: string } {
  const endExclusive = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
  ));
  const startInclusive = new Date(endExclusive.getTime() - (7 * 86_400_000));
  return {
    from: startInclusive.toISOString().slice(0, 10),
    to: endExclusive.toISOString().slice(0, 10),
  };
}

export function isMetaReconnectRequiredError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /meta ads authorization expired|meta ads connection needs reconnect|token_expired/i.test(message);
}

export function MetaImportRefreshPage({
  projectId,
  client,
  onBack,
  onOpenIntegrations,
}: MetaImportRefreshPageProps): React.ReactElement {
  const { locales: projectLocales, loading: localesLoading } = useProjectLocales();
  const [connected, setConnected] = useState<boolean | null>(null);
  const [accounts, setAccounts] = useState<Array<{ id: string; name?: string }>>([]);
  const [accountId, setAccountId] = useState('');
  const [allCurrentScope, setAllCurrentScope] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(new Set());
  const [campaigns, setCampaigns] = useState<MetaAdsCampaignSummary[]>([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<Set<string>>(new Set());
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsLoadingMore, setCampaignsLoadingMore] = useState(false);
  const [campaignNextCursor, setCampaignNextCursor] = useState<string | null>(null);
  const [campaignInventoryLoaded, setCampaignInventoryLoaded] = useState(false);
  const [sources, setSources] = useState<AdCreativeProviderSourceHead[]>([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<string>>(new Set());
  const [taxonomyPreflight, setTaxonomyPreflight] = useState<AdCreativeProviderSourceTaxonomyPreflight | null>(null);
  const [taxonomyConfirmed, setTaxonomyConfirmed] = useState(false);
  const [taxonomyBusy, setTaxonomyBusy] = useState<'preflight' | 'start' | 'review' | null>(null);
  const [activeTaxonomyTaskId, setActiveTaxonomyTaskId] = useState<string | null>(null);
  const [taxonomyCurrent, setTaxonomyCurrent] = useState<AdCreativeProviderSourceTaxonomyCurrent | null>(null);
  const [taxonomyDraft, setTaxonomyDraft] = useState<AdCreativeProviderSourceTaxonomyFields | null>(null);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesLoadingMore, setSourcesLoadingMore] = useState(false);
  const [sourceNextCursor, setSourceNextCursor] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<AdCreativeProviderSourceCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesLoadingMore, setCandidatesLoadingMore] = useState(false);
  const [candidateNextCursor, setCandidateNextCursor] = useState<string | null>(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<Set<string>>(new Set());
  const [candidatePreflight, setCandidatePreflight] = useState<AdCreativeProviderSourceSyncPreflight | null>(null);
  const [candidatePreflightIds, setCandidatePreflightIds] = useState<string[]>([]);
  const [candidatePreflighting, setCandidatePreflighting] = useState(false);
  const [candidateConfirmed, setCandidateConfirmed] = useState(false);
  const [syncConfirmed, setSyncConfirmed] = useState(false);
  const [sourceSyncPreflight, setSourceSyncPreflight] = useState<AdCreativeProviderSourceSyncPreflight | null>(null);
  const [preflighting, setPreflighting] = useState(false);
  const [preflightExpired, setPreflightExpired] = useState(false);
  const [mirrorMedia, setMirrorMedia] = useState(true);
  const [onlySegmentlyAssociated, setOnlySegmentlyAssociated] = useState(true);
  const [associationFrom, setAssociationFrom] = useState(
    () => defaultProviderSourceAssociationWindow().from,
  );
  const [associationTo, setAssociationTo] = useState(
    () => defaultProviderSourceAssociationWindow().to,
  );
  const [syncing, setSyncing] = useState(false);
  const [activeSync, setActiveSync] = useState<{ syncRunId: string; taskId: string } | null>(null);
  const [syncResult, setSyncResult] = useState<AdCreativeProviderSourceSyncProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdCreativeProviderSourceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sourceLocale, setSourceLocale] = useState('');
  const [targetLocales, setTargetLocales] = useState<Set<string>>(new Set());
  const [deploymentContext, setDeploymentContext] = useState('');
  const [selectedBaselineCandidateId, setSelectedBaselineCandidateId] = useState('');
  const [bindingsLoadingMore, setBindingsLoadingMore] = useState(false);
  const [languageEvidenceLoadingMore, setLanguageEvidenceLoadingMore] = useState(false);
  const [baselineBusy, setBaselineBusy] = useState(false);
  const [languageBusy, setLanguageBusy] = useState(false);
  const [languageDetectionBusy, setLanguageDetectionBusy] = useState<'preflight' | 'start' | null>(null);
  const [languageDetectionPreflight, setLanguageDetectionPreflight] = useState<AdCreativeProviderSourceLanguageDetectionPreflight | null>(null);
  const [languageDetectionConfirmed, setLanguageDetectionConfirmed] = useState(false);
  const [languageDetectionError, setLanguageDetectionError] = useState<string | null>(null);
  const [activeLanguageDetection, setActiveLanguageDetection] = useState<{
    taskId: string;
    sourceId: string;
    snapshotId: string;
    status: AdCreativeProviderSourceLanguageDetectionTaskStatus;
  } | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const [createdDraft, setCreatedDraft] = useState<{
    itemId: string;
    sourceSnapshotId: string;
    providerBaselineEvidenceId?: string;
    generatedAssetCount: number;
    providerMutationCount: number;
    reused: boolean;
  } | null>(null);
  const [draftIdempotencyKey, setDraftIdempotencyKey] = useState('');
  const preflightEpochRef = useRef(0);
  const handledLanguageDetectionTerminalRef = useRef<string | null>(null);
  const { task: sourceSyncTask, error: sourceSyncTaskError } = useBackendTask(
    activeSync ? projectId : null,
    activeSync?.taskId ?? null,
  );
  const { task: languageDetectionTask } = useTask(activeLanguageDetection?.taskId ?? null);
  const { task: taxonomyTask } = useTask(activeTaxonomyTaskId);

  const syncInProgress = Boolean(activeSync && (!syncResult || ![
    'completed', 'failed', 'cancelled', 'needs_reauth', 'paused_quota', 'retrying',
  ].includes(syncResult.status)));

  const invalidateSourceSyncPreflight = useCallback(() => {
    preflightEpochRef.current += 1;
    setSourceSyncPreflight(null);
    setPreflighting(false);
    setPreflightExpired(false);
    setSyncConfirmed(false);
  }, []);

  useEffect(() => {
    setPreflightExpired(false);
    if (!sourceSyncPreflight) return undefined;
    const expiresAt = Date.parse(sourceSyncPreflight.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      setPreflightExpired(true);
      setSyncConfirmed(false);
      return undefined;
    }
    const timeout = window.setTimeout(() => {
      setPreflightExpired(true);
      setSyncConfirmed(false);
    }, Math.min(expiresAt - Date.now(), 2_147_483_647));
    return () => window.clearTimeout(timeout);
  }, [sourceSyncPreflight]);

  const loadSources = useCallback(async (providerAccountId?: string, cursor?: string) => {
    if (cursor) setSourcesLoadingMore(true);
    else setSourcesLoading(true);
    try {
      const response = await client.listProviderCreativeSources(projectId, {
        providerAccountId: providerAccountId || undefined,
        limit: 100,
        cursor,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not load synced creative sources.');
      }
      setSources((current) => cursor
        ? mergeProviderSourcePages(current, response.data!.sources)
        : response.data!.sources);
      setSourceNextCursor(response.data.nextCursor ?? null);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      if (cursor) setSourcesLoadingMore(false);
      else setSourcesLoading(false);
    }
  }, [client, projectId]);

  const loadSourceTaxonomy = useCallback(async (sourceId: string) => {
    const response = await client.getProviderCreativeSourceTaxonomy(projectId, sourceId);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Could not load the creative taxonomy.');
    }
    setTaxonomyCurrent(response.data);
    setTaxonomyDraft(response.data.taxonomy ? {
      topic: { ...response.data.taxonomy.topic },
      angle: { ...response.data.taxonomy.angle },
      concept: { ...response.data.taxonomy.concept },
      hook: { ...response.data.taxonomy.hook },
      proof: {
        ...response.data.taxonomy.proof,
        evidenceEntryKeys: [...response.data.taxonomy.proof.evidenceEntryKeys],
      },
      format: { ...response.data.taxonomy.format },
      confidence: response.data.taxonomy.confidence,
      warnings: [...response.data.taxonomy.warnings],
    } : null);
  }, [client, projectId]);

  const preflightTaxonomy = async () => {
    if (taxonomyBusy || selectedSourceIds.size < 1 || selectedSourceIds.size > 20) return;
    setTaxonomyBusy('preflight');
    setTaxonomyPreflight(null);
    setTaxonomyConfirmed(false);
    setError(null);
    try {
      const response = await client.preflightProviderCreativeSourceTaxonomy({
        projectId,
        sourceIds: Array.from(selectedSourceIds),
      });
      if (!response.success || !response.data?.preflight) {
        throw new Error(response.error || 'Could not review the taxonomy analysis scope.');
      }
      setTaxonomyPreflight(response.data.preflight);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setTaxonomyBusy(null);
    }
  };

  const startTaxonomy = async () => {
    if (!taxonomyPreflight || !taxonomyConfirmed || taxonomyBusy) return;
    if (Date.parse(taxonomyPreflight.expiresAt) <= Date.now()) {
      setTaxonomyConfirmed(false);
      setError('This taxonomy estimate expired. Review the selected creatives again.');
      return;
    }
    setTaxonomyBusy('start');
    setError(null);
    try {
      const response = await client.startProviderCreativeSourceTaxonomy({
        projectId,
        preflightToken: taxonomyPreflight.preflightToken,
        expectedInputHash: taxonomyPreflight.inputHash,
        model: taxonomyPreflight.model,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not start taxonomy analysis.');
      }
      setActiveTaxonomyTaskId(response.data.taskId);
      setTaxonomyConfirmed(false);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setTaxonomyBusy(null);
    }
  };

  const reviewTaxonomy = async (decision: 'approve' | 'reject') => {
    if (!detail || !taxonomyCurrent?.taxonomy || taxonomyBusy) return;
    setTaxonomyBusy('review');
    setError(null);
    try {
      const response = await client.reviewProviderCreativeSourceTaxonomy({
        projectId,
        sourceId: detail.source.id,
        sourceSnapshotId: detail.currentSnapshot.id,
        taxonomySnapshotId: taxonomyCurrent.taxonomy.id,
        decision,
        ...(decision === 'approve' && taxonomyDraft ? { taxonomy: taxonomyDraft } : {}),
      });
      if (!response.success || !response.data?.taxonomy) {
        throw new Error(response.error || 'Could not save the taxonomy review.');
      }
      await Promise.all([
        loadSourceTaxonomy(detail.source.id),
        loadSources(accountId || undefined),
      ]);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setTaxonomyBusy(null);
    }
  };

  useEffect(() => {
    if (!activeTaxonomyTaskId || !taxonomyTask?.status) return;
    if (!['completed', 'error', 'cancelled'].includes(taxonomyTask.status)) return;
    if (taxonomyTask.status === 'completed') {
      void loadSources(accountId || undefined);
      if (selectedSourceId) void loadSourceTaxonomy(selectedSourceId).catch((nextError) => {
        setError(nextError instanceof Error ? nextError.message : String(nextError));
      });
      setSelectedSourceIds(new Set());
      setTaxonomyPreflight(null);
    } else if (taxonomyTask.error) {
      const taskError = taxonomyTask.error as unknown;
      setError(typeof taskError === 'string'
        ? taskError
        : taskError && typeof taskError === 'object' && 'message' in taskError
          ? String((taskError as { message: unknown }).message)
          : 'Taxonomy analysis failed.');
    }
    setActiveTaxonomyTaskId(null);
  }, [accountId, activeTaxonomyTaskId, loadSourceTaxonomy, loadSources, selectedSourceId, taxonomyTask]);

  const loadCandidates = useCallback(async (providerAccountId: string, cursor?: string) => {
    if (!providerAccountId) return;
    if (cursor) setCandidatesLoadingMore(true);
    else setCandidatesLoading(true);
    try {
      const response = await client.listProviderCreativeSourceCandidates(projectId, providerAccountId, {
        limit: 100,
        cursor,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not load discovery candidates.');
      }
      setCandidates((current) => cursor
        ? mergeProviderCandidatePages(current, response.data!.candidates)
        : response.data!.candidates);
      setCandidateNextCursor(response.data.nextCursor ?? null);
      if (!cursor) setSelectedCandidateIds(new Set());
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      if (cursor) setCandidatesLoadingMore(false);
      else setCandidatesLoading(false);
    }
  }, [client, projectId]);

  useEffect(() => {
    if (!activeSync) return undefined;
    let cancelled = false;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const response = await client.getProviderCreativeSourceSync(projectId, activeSync.syncRunId);
        if (cancelled) return;
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Could not read creative source-sync progress.');
        }
        setSyncResult(response.data);
        if (!['pending', 'running', 'cancelling'].includes(response.data.status)) {
          await Promise.all([
            loadSources(accountId || undefined),
            accountId ? loadCandidates(accountId) : Promise.resolve(),
          ]);
        } else {
          timer = window.setTimeout(() => void poll(), 1_500);
        }
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError instanceof Error ? nextError.message : String(nextError));
          timer = window.setTimeout(() => void poll(), 3_000);
        }
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [
    accountId,
    activeSync,
    client,
    loadCandidates,
    loadSources,
    projectId,
    sourceSyncTask?.status,
    sourceSyncTask?.updatedAt,
  ]);

  useEffect(() => {
    if (sourceSyncTaskError) setError(sourceSyncTaskError.message);
  }, [sourceSyncTaskError]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [status, sourceResponse] = await Promise.all([
          client.getMetaAdsStatus(projectId),
          client.listProviderCreativeSources(projectId, { limit: 100 }),
        ]);
        if (cancelled) return;
        const isConnected = Boolean(status.connected && !status.needsReconnect);
        setConnected(isConnected);
        // A connected account is loaded again below with its exact account scope.
        // Showing the unscoped page first creates a brief selectable list whose
        // selection is then cleared when the account-scoped request finishes.
        if (!isConnected && sourceResponse.success && sourceResponse.data) {
          setSources(sourceResponse.data.sources);
          setSourceNextCursor(sourceResponse.data.nextCursor ?? null);
        }
        if (!isConnected) return;
        const accountsResponse = await client.listMetaAdAccounts(projectId);
        if (cancelled) return;
        const nextAccounts = accountsResponse.accounts ?? status.accessibleAdAccounts ?? [];
        setAccounts(nextAccounts);
        setAccountId(nextAccounts[0]?.id ?? '');
        setSelectedAccountIds(new Set(nextAccounts[0]?.id ? [nextAccounts[0].id] : []));
      } catch (nextError) {
        if (!cancelled) setError(nextError instanceof Error ? nextError.message : String(nextError));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, projectId]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    invalidateSourceSyncPreflight();
    setCampaignsLoading(true);
    setCampaigns([]);
    setSelectedCampaignIds(new Set());
    setCampaignNextCursor(null);
    setCampaignInventoryLoaded(false);
    setSourceNextCursor(null);
    setCandidates([]);
    setCandidateNextCursor(null);
    setSelectedCandidateIds(new Set());
    setCandidatePreflight(null);
    setCandidatePreflightIds([]);
    setCandidateConfirmed(false);
    setSelectedSourceIds(new Set());
    setTaxonomyPreflight(null);
    setTaxonomyConfirmed(false);
    void Promise.all([
      client.listMetaCampaigns(projectId, accountId, { limit: 100 }),
      client.listProviderCreativeSources(projectId, { providerAccountId: accountId, limit: 100 }),
      client.listProviderCreativeSourceCandidates(projectId, accountId, { limit: 100 }),
    ]).then(([campaignResponse, sourceResponse, candidateResponse]) => {
      if (cancelled) return;
      if (campaignResponse.error) throw new Error(campaignResponse.error);
      const nextCampaigns = campaignResponse.campaigns ?? [];
      if (campaignResponse.hasMore && !campaignResponse.nextCursor) {
        throw new Error('Meta reported more campaigns without a resumable cursor. Campaign selection is blocked.');
      }
      setCampaigns(nextCampaigns);
      setSelectedCampaignIds(new Set(nextCampaigns.map((campaign) => campaign.id)));
      setCampaignNextCursor(campaignResponse.nextCursor ?? null);
      setCampaignInventoryLoaded(true);
      if (sourceResponse.success && sourceResponse.data) {
        setSources(sourceResponse.data.sources);
        setSourceNextCursor(sourceResponse.data.nextCursor ?? null);
      }
      if (candidateResponse.success && candidateResponse.data) {
        setCandidates(candidateResponse.data.candidates);
        setCandidateNextCursor(candidateResponse.data.nextCursor ?? null);
      } else if (!candidateResponse.success) {
        throw new Error(candidateResponse.error || 'Could not load discovery candidates.');
      }
    }).catch((nextError) => {
      if (!cancelled) {
        setError(nextError instanceof Error ? nextError.message : String(nextError));
        if (isMetaReconnectRequiredError(nextError)) setConnected(false);
      }
    }).finally(() => {
      if (!cancelled) setCampaignsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [accountId, client, invalidateSourceSyncPreflight, projectId]);

  const selectedCampaignCount = selectedCampaignIds.size;
  const allCampaignsSelected = campaigns.length > 0 && selectedCampaignCount === campaigns.length;
  const campaignInventoryComplete = campaignInventoryLoaded
    && !campaignsLoading
    && !campaignsLoadingMore
    && !campaignNextCursor;
  const campaignSubsetTooLarge = selectedCampaignCount > 100;
  const funnelAssociationScope = mirrorMedia && onlySegmentlyAssociated
    ? 'segmently_associated' as const
    : 'all' as const;
  const associationWindowValid = funnelAssociationScope !== 'segmently_associated'
    || (Boolean(associationFrom) && Boolean(associationTo) && associationFrom < associationTo);
  const canPreflight = Boolean(
    connected
    && !preflighting
    && !syncing
    && !syncInProgress
    && associationWindowValid
    && (allCurrentScope
      ? selectedAccountIds.size > 0 && selectedAccountIds.size <= 10
      : accountId
        && campaignInventoryComplete
        && !campaignSubsetTooLarge
        && selectedCampaignCount > 0),
  );
  const zeroAssociatedPreflight = Boolean(
    sourceSyncPreflight
    && sourceSyncPreflight.totals.ads > 0
    && sourceSyncPreflight.funnelAssociation.scope === 'segmently_associated'
    && sourceSyncPreflight.totals.includedBindings === 0,
  );
  const emptySelectedPreflight = Boolean(
    sourceSyncPreflight
    && sourceSyncPreflight.totals.ads === 0,
  );
  const canSync = Boolean(
    sourceSyncPreflight
    && !preflightExpired
    && !emptySelectedPreflight
    && !zeroAssociatedPreflight
    && syncConfirmed
    && !syncing
    && !syncInProgress,
  );
  const candidatePreflightExpired = Boolean(
    candidatePreflight
    && (!Number.isFinite(Date.parse(candidatePreflight.expiresAt))
      || Date.parse(candidatePreflight.expiresAt) <= Date.now()),
  );
  const canPreflightCandidates = Boolean(
    connected
    && accountId
    && selectedCandidateIds.size > 0
    && selectedCandidateIds.size <= 100
    && associationFrom
    && associationTo
    && associationFrom < associationTo
    && !candidatePreflighting
    && !syncing
    && !syncInProgress,
  );
  const canSyncCandidates = Boolean(
    candidatePreflight
    && candidatePreflightIds.length > 0
    && !candidatePreflightExpired
    && candidateConfirmed
    && !syncing
    && !syncInProgress,
  );

  const loadMoreCampaigns = async () => {
    if (!campaignNextCursor || campaignsLoadingMore) return;
    const cursor = campaignNextCursor;
    const selectIncoming = allCampaignsSelected;
    setCampaignsLoadingMore(true);
    setError(null);
    try {
      const response = await client.listMetaCampaigns(projectId, accountId, { limit: 100, cursor });
      if (response.error) throw new Error(response.error);
      if (response.hasMore && !response.nextCursor) {
        throw new Error('Meta reported more campaigns without a resumable cursor. Campaign selection is blocked.');
      }
      const incoming = response.campaigns ?? [];
      setCampaigns((current) => mergeById(current, incoming));
      if (selectIncoming) {
        setSelectedCampaignIds((current) => new Set([
          ...current,
          ...incoming.map((campaign) => campaign.id),
        ]));
      }
      setCampaignNextCursor(response.nextCursor ?? null);
      invalidateSourceSyncPreflight();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
      if (isMetaReconnectRequiredError(nextError)) setConnected(false);
    } finally {
      setCampaignsLoadingMore(false);
    }
  };

  const toggleCampaign = (campaignId: string) => {
    setSelectedCampaignIds((current) => {
      const next = new Set(current);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
    invalidateSourceSyncPreflight();
  };

  const runPreflight = async () => {
    if (!canPreflight) return;
    invalidateSourceSyncPreflight();
    const requestEpoch = preflightEpochRef.current;
    setPreflighting(true);
    setError(null);
    try {
      const response = await client.preflightMetaCreativeSourceSync({
        projectId,
        provider: 'meta',
        providerAccountIds: allCurrentScope
          ? Array.from(selectedAccountIds).sort()
          : [accountId],
        scope: allCurrentScope ? {
          mode: 'all_current',
          includeInactiveAds: true,
        } : {
          mode: 'selected_campaigns',
          campaignIds: Array.from(selectedCampaignIds).sort(),
          includeInactiveAds: true,
        },
        funnelAssociationScope,
        mirrorMedia,
        ...(funnelAssociationScope === 'segmently_associated'
          ? { associationWindow: { from: associationFrom, to: associationTo } }
          : {}),
      });
      if (requestEpoch !== preflightEpochRef.current) return;
      if (!response.success || !response.data?.preflight) {
        throw new Error(response.error || 'Meta creative source preflight could not be completed.');
      }
      setSourceSyncPreflight(response.data.preflight);
    } catch (nextError) {
      if (requestEpoch === preflightEpochRef.current) {
        setError(nextError instanceof Error ? nextError.message : String(nextError));
        if (isMetaReconnectRequiredError(nextError)) setConnected(false);
      }
    } finally {
      if (requestEpoch === preflightEpochRef.current) setPreflighting(false);
    }
  };

  const runSync = async () => {
    if (!canSync || !sourceSyncPreflight) return;
    setSyncing(true);
    setError(null);
    setSyncResult(null);
    try {
      const response = await client.syncMetaCreativeSources({
        projectId,
        preflightToken: sourceSyncPreflight.preflightToken,
        idempotencyKey: `phase-zero-ui:${sourceSyncPreflight.scopeDigest}:${sourceSyncPreflight.issuedAt}`,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Meta creative source sync could not be started.');
      }
      setActiveSync({ syncRunId: response.data.syncRunId, taskId: response.data.taskId });
      setSyncConfirmed(false);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
      if (isMetaReconnectRequiredError(nextError)) setConnected(false);
    } finally {
      setSyncing(false);
    }
  };

  const toggleCandidate = (candidateId: string) => {
    setSelectedCandidateIds((current) => {
      const next = new Set(current);
      if (next.has(candidateId)) next.delete(candidateId);
      else if (next.size < 100) next.add(candidateId);
      return next;
    });
    setCandidatePreflight(null);
    setCandidatePreflightIds([]);
    setCandidateConfirmed(false);
  };

  const loadMoreCandidates = async () => {
    if (!candidateNextCursor || candidatesLoadingMore || !accountId) return;
    await loadCandidates(accountId, candidateNextCursor);
  };

  const runCandidatePreflight = async () => {
    if (!canPreflightCandidates) return;
    setCandidatePreflighting(true);
    setCandidatePreflight(null);
    setCandidatePreflightIds([]);
    setCandidateConfirmed(false);
    setError(null);
    try {
      const candidateIds = Array.from(selectedCandidateIds).sort();
      const response = await client.preflightProviderCreativeSourceCandidatePromotion({
        projectId,
        candidateIds,
        associationWindow: { from: associationFrom, to: associationTo },
      });
      if (!response.success || !response.data?.preflight) {
        throw new Error(response.error || 'Could not review the selected discovery candidates.');
      }
      if (response.data.candidateIds.length !== candidateIds.length
        || response.data.candidateIds.some((id) => !selectedCandidateIds.has(id))) {
        throw new Error('The server reviewed a different discovery-candidate selection. Select them again.');
      }
      setCandidatePreflight(response.data.preflight);
      setCandidatePreflightIds(response.data.candidateIds);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
      if (isMetaReconnectRequiredError(nextError)) setConnected(false);
    } finally {
      setCandidatePreflighting(false);
    }
  };

  const runCandidateSync = async () => {
    if (!canSyncCandidates || !candidatePreflight) return;
    setSyncing(true);
    setError(null);
    setSyncResult(null);
    try {
      const response = await client.syncMetaCreativeSources({
        projectId,
        preflightToken: candidatePreflight.preflightToken,
        idempotencyKey: `phase-zero-candidates:${candidatePreflight.scopeDigest}:${candidatePreflight.issuedAt}`,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'The selected discovery candidates could not be synced.');
      }
      setActiveSync({ syncRunId: response.data.syncRunId, taskId: response.data.taskId });
      setCandidateConfirmed(false);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
      if (isMetaReconnectRequiredError(nextError)) setConnected(false);
    } finally {
      setSyncing(false);
    }
  };

  const cancelSync = async () => {
    if (!activeSync || !syncResult?.canCancel || syncing) return;
    setSyncing(true);
    setError(null);
    try {
      const response = await client.cancelProviderCreativeSourceSync(projectId, activeSync.syncRunId);
      if (!response.success || !response.data) throw new Error(response.error || 'Could not cancel source sync.');
      setSyncResult(response.data);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setSyncing(false);
    }
  };

  const resumeSync = async () => {
    if (!activeSync || !syncResult?.canResume || syncing) return;
    setSyncing(true);
    setError(null);
    try {
      const response = await client.resumeProviderCreativeSourceSync(projectId, activeSync.syncRunId);
      if (!response.success || !response.data) throw new Error(response.error || 'Could not resume source sync.');
      setSyncResult(response.data);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setSyncing(false);
    }
  };

  const refreshLanguageDetectionDetail = useCallback(async (sourceId: string) => {
    try {
      const response = await client.getProviderCreativeSource(projectId, sourceId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not refresh the language detector result.');
      }
      const nextDetail = response.data;
      setDetail((current) => current?.source.id === sourceId ? nextDetail : current);
      setSources((current) => current.map((source) => (
        source.id === sourceId ? nextDetail.source : source
      )));
      setLanguageDetectionError(null);
    } catch (nextError) {
      setLanguageDetectionError(nextError instanceof Error ? nextError.message : String(nextError));
    }
  }, [client, projectId]);

  const handleLanguageDetectionTask = useCallback((task: BackendTask) => {
    const status = task.status as AdCreativeProviderSourceLanguageDetectionTaskStatus;
    setActiveLanguageDetection((current) => (
      current?.taskId === task.id && current.status !== status ? { ...current, status } : current
    ));
    if (!TERMINAL_LANGUAGE_DETECTION_STATUSES.has(status)) return;

    const terminalKey = `${task.id}:${status}`;
    if (handledLanguageDetectionTerminalRef.current === terminalKey) return;
    handledLanguageDetectionTerminalRef.current = terminalKey;
    setLanguageDetectionPreflight(null);
    setLanguageDetectionConfirmed(false);

    if (status === 'completed' || status === 'error') {
      const sourceId = activeLanguageDetection?.taskId === task.id
        ? activeLanguageDetection.sourceId
        : selectedSourceId;
      if (sourceId) void refreshLanguageDetectionDetail(sourceId);
      return;
    }
    setLanguageDetectionError(
      status === 'cancelled'
        ? 'Language detection was cancelled before completion. You can request a new estimate.'
        : 'Language detection ended without a usable suggestion. Choose and confirm a project language manually below.',
    );
  }, [activeLanguageDetection, refreshLanguageDetectionDetail, selectedSourceId]);

  useTaskTransition({
    filter: (task) => Boolean(activeLanguageDetection && task.id === activeLanguageDetection.taskId),
    onTransition: (task) => handleLanguageDetectionTask(task),
  });

  // A reused/very fast task may already be terminal before the transition
  // subscription sees it. This is a store read, not an API polling fallback.
  useEffect(() => {
    if (languageDetectionTask) handleLanguageDetectionTask(languageDetectionTask);
  }, [handleLanguageDetectionTask, languageDetectionTask]);

  const openSource = async (sourceId: string) => {
    setSelectedSourceId(sourceId);
    setDetail(null);
    setDetailLoading(true);
    setCreatedDraft(null);
    setTargetLocales(new Set());
    setSourceLocale('');
    setDeploymentContext('');
    setSelectedBaselineCandidateId('');
    setBindingsLoadingMore(false);
    setLanguageEvidenceLoadingMore(false);
    setLanguageDetectionBusy(null);
    setLanguageDetectionPreflight(null);
    setLanguageDetectionConfirmed(false);
    setLanguageDetectionError(null);
    setActiveLanguageDetection(null);
    setTaxonomyCurrent(null);
    setTaxonomyDraft(null);
    handledLanguageDetectionTerminalRef.current = null;
    setDraftIdempotencyKey(`phase-zero-handoff:${sourceId}:${Date.now()}`);
    try {
      const response = await client.getProviderCreativeSource(projectId, sourceId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not load the creative source.');
      }
      const nextDetail = response.data;
      setDetail(nextDetail);
      const projectedLocale = nextDetail.source.sourceLanguage.locale;
      setSourceLocale(
        nextDetail.source.sourceLanguage.origin === 'provider_declared'
        && projectLocales.some((locale) => locale.code === projectedLocale)
          ? projectedLocale!
          : '',
      );
      const latestDetection = nextDetail.languageDetection;
      if (latestDetection?.latestTaskId && latestDetection.latestTaskStatus
        && !TERMINAL_LANGUAGE_DETECTION_STATUSES.has(latestDetection.latestTaskStatus)) {
        setActiveLanguageDetection({
          taskId: latestDetection.latestTaskId,
          sourceId,
          snapshotId: latestDetection.snapshotId,
          status: latestDetection.latestTaskStatus,
        });
      }
      if (nextDetail.bindings.length === 1 && !nextDetail.bindingsPage.hasMore) {
        setDeploymentContext(nextDetail.bindings[0].id);
      }
      await loadSourceTaxonomy(sourceId);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    if (bindingsLoadingMore || languageEvidenceLoadingMore || baselineBusy || languageBusy || draftBusy) return;
    setSelectedSourceId(null);
    setDetail(null);
    setCreatedDraft(null);
    setLanguageDetectionPreflight(null);
    setLanguageDetectionConfirmed(false);
    setLanguageDetectionError(null);
    setActiveLanguageDetection(null);
    setTaxonomyCurrent(null);
    setTaxonomyDraft(null);
  };

  const loadMoreBindings = async () => {
    const cursor = detail?.bindingsPage.nextCursor;
    if (!detail || !cursor || bindingsLoadingMore) return;
    const sourceId = detail.source.id;
    setBindingsLoadingMore(true);
    setError(null);
    try {
      const response = await client.listProviderCreativeSourceBindings(projectId, sourceId, {
        limit: detail.bindingsPage.limit,
        cursor,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not load more exact Meta ad bindings.');
      }
      setDetail((current) => {
        if (!current
          || current.source.id !== sourceId
          || current.currentSnapshot.id !== response.data!.currentSnapshotId) return current;
        return {
          ...current,
          bindings: mergeById(current.bindings, response.data!.bindings),
          bindingsPage: providerDetailPageTruth(response.data!),
        };
      });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setBindingsLoadingMore(false);
    }
  };

  const loadMoreLanguageEvidence = async () => {
    const cursor = detail?.languageEvidencePage.nextCursor;
    if (!detail || !cursor || languageEvidenceLoadingMore) return;
    const sourceId = detail.source.id;
    setLanguageEvidenceLoadingMore(true);
    setError(null);
    try {
      const response = await client.listProviderCreativeSourceLanguageEvidence(projectId, sourceId, {
        limit: detail.languageEvidencePage.limit,
        cursor,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not load more immutable source-language evidence.');
      }
      setDetail((current) => {
        if (!current
          || current.source.id !== sourceId
          || current.currentSnapshot.id !== response.data!.currentSnapshotId) return current;
        return {
          ...current,
          languageEvidence: mergeById(current.languageEvidence, response.data!.languageEvidence),
          languageEvidencePage: providerDetailPageTruth(response.data!),
        };
      });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLanguageEvidenceLoadingMore(false);
    }
  };

  const preflightLanguageDetection = async () => {
    if (!detail || languageDetectionBusy) return;
    setLanguageDetectionBusy('preflight');
    setLanguageDetectionPreflight(null);
    setLanguageDetectionConfirmed(false);
    setLanguageDetectionError(null);
    handledLanguageDetectionTerminalRef.current = null;
    try {
      const response = await client.preflightProviderCreativeSourceLanguageDetection({
        projectId,
        sourceId: detail.source.id,
        snapshotId: detail.currentSnapshot.id,
      });
      if (!response.success || !response.data?.preflight) {
        throw new Error(response.error || 'Could not preflight source-language detection.');
      }
      if (response.data.preflight.snapshotId !== detail.currentSnapshot.id) {
        throw new Error('The source snapshot changed. Reopen the source before detecting its language.');
      }
      setLanguageDetectionPreflight(response.data.preflight);
    } catch (nextError) {
      setLanguageDetectionError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLanguageDetectionBusy(null);
    }
  };

  const startLanguageDetection = async () => {
    if (!detail || languageDetectionBusy || !languageDetectionConfirmed
      || languageDetectionPreflight?.decision !== 'ai_required') return;
    const expiresAt = Date.parse(languageDetectionPreflight.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      setLanguageDetectionConfirmed(false);
      setLanguageDetectionError('This language-detection estimate expired. Run a new preflight.');
      return;
    }

    setLanguageDetectionBusy('start');
    setLanguageDetectionError(null);
    try {
      const response = await client.startProviderCreativeSourceLanguageDetection({
        projectId,
        sourceId: detail.source.id,
        snapshotId: detail.currentSnapshot.id,
        model: languageDetectionPreflight.model,
        preflightToken: languageDetectionPreflight.preflightToken,
        expectedInputManifestHash: languageDetectionPreflight.inputManifestHash,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not start source-language detection.');
      }
      if (response.data.snapshotId !== detail.currentSnapshot.id
        || response.data.inputManifestHash !== languageDetectionPreflight.inputManifestHash) {
        throw new Error('Language detection did not accept the exact preflight input. Run a new preflight.');
      }
      const nextTask = {
        taskId: response.data.taskId,
        sourceId: detail.source.id,
        snapshotId: response.data.snapshotId,
        status: response.data.status,
      };
      setActiveLanguageDetection(nextTask);
      setLanguageDetectionConfirmed(false);
      handledLanguageDetectionTerminalRef.current = null;
      if (response.data.status === 'completed') {
        handledLanguageDetectionTerminalRef.current = `${response.data.taskId}:completed`;
        setLanguageDetectionPreflight(null);
        await refreshLanguageDetectionDetail(detail.source.id);
      }
    } catch (nextError) {
      setLanguageDetectionError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLanguageDetectionBusy(null);
    }
  };

  const freezeBaseline = async () => {
    if (!detail || !deploymentContext || !selectedBaselineChoice || !selectedBaselineChoice.eligible) return;
    setBaselineBusy(true);
    setError(null);
    try {
      const response = await client.selectProviderCreativeSourceBaseline({
        projectId,
        sourceId: detail.source.id,
        snapshotId: detail.currentSnapshot.id,
        sourceBindingId: deploymentContext,
        candidateId: selectedBaselineChoice.id,
        candidateHash: selectedBaselineChoice.candidateHash,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not freeze the selected provider baseline.');
      }
      setDetail((current) => current ? {
        ...current,
        baselineEvidence: current.baselineEvidence.some((evidence) => evidence.id === response.data!.evidence.id)
          ? current.baselineEvidence
          : [response.data!.evidence, ...current.baselineEvidence],
      } : current);
      setCreatedDraft(null);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setBaselineBusy(false);
    }
  };

  const confirmLanguage = async () => {
    if (!detail || !sourceLocale || !deploymentContext || !baselineReady) return;
    setLanguageBusy(true);
    setError(null);
    try {
      const response = await client.confirmProviderCreativeSourceLanguage({
        projectId,
        sourceId: detail.source.id,
        snapshotId: detail.currentSnapshot.id,
        sourceBindingId: deploymentContext,
        ...(selectedBaselineEvidence ? { providerBaselineEvidenceId: selectedBaselineEvidence.id } : {}),
        locale: sourceLocale,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not confirm the source language.');
      }
      setDetail((current) => current ? {
        ...current,
        source: response.data!.source,
        languageEvidence: current.languageEvidence.some((evidence) => evidence.id === response.data!.evidence.id)
          ? current.languageEvidence
          : [response.data!.evidence, ...current.languageEvidence],
      } : current);
      setSources((current) => current.map((source) => (
        source.id === response.data!.source.id ? response.data!.source : source
      )));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLanguageBusy(false);
    }
  };

  const toggleTargetLocale = (locale: string) => {
    setTargetLocales((current) => {
      const next = new Set(current);
      if (next.has(locale)) next.delete(locale);
      else next.add(locale);
      return next;
    });
  };

  const createDraft = async () => {
    if (!detail || !sourceLocale || !deploymentContext
      || !baselineReady
      || !isExactSnapshotLanguageConfirmed(
        detail,
        sourceLocale,
        deploymentContext,
        selectedBaselineEvidence?.id,
      )) return;
    setDraftBusy(true);
    setError(null);
    try {
      const response = await client.createProviderCreativeVariationDraft({
        projectId,
        sourceId: detail.source.id,
        snapshotId: detail.currentSnapshot.id,
        sourceBindingId: deploymentContext,
        ...(selectedBaselineEvidence ? { providerBaselineEvidenceId: selectedBaselineEvidence.id } : {}),
        sourceLocale,
        targetLocales: Array.from(targetLocales).filter((locale) => locale !== sourceLocale).sort(),
        idempotencyKey: draftIdempotencyKey,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Could not create the Segmently variation draft.');
      }
      setCreatedDraft({
        itemId: response.data.item.id,
        sourceSnapshotId: response.data.sourceSnapshotId,
        providerBaselineEvidenceId: response.data.providerBaselineEvidenceId,
        generatedAssetCount: response.data.generatedAssetCount,
        providerMutationCount: response.data.providerMutationCount,
        reused: response.data.reused,
      });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setDraftBusy(false);
    }
  };

  const baselineRequired = Boolean(detail && (
    detail.currentSnapshot.displayProjection.presentationState !== 'exact_static'
    || detail.currentSnapshot.displayProjection.carouselCardCount > 0
    || detail.currentSnapshot.displayProjection.primaryTextCandidates.length > 1
    || detail.currentSnapshot.displayProjection.headlineCandidates.length > 1
    || detail.currentSnapshot.displayProjection.descriptionCandidates.length > 1
  ));
  const selectedBaselineChoice = detail?.baselineChoices.find((choice) => choice.id === selectedBaselineCandidateId);
  const selectedBaselineEvidence = detail?.baselineEvidence.find((evidence) => (
    evidence.sourceSnapshotId === detail.currentSnapshot.id
    && evidence.sourceBindingId === deploymentContext
    && evidence.candidateId === selectedBaselineCandidateId
    && evidence.candidateHash === selectedBaselineChoice?.candidateHash
  ));
  const baselineReady = !baselineRequired || Boolean(selectedBaselineEvidence);
  const languageState = detail ? providerSourceLanguageUiState(detail.source.sourceLanguage) : 'unknown';
  const latestLanguageDetectionSuggestion = detail?.languageDetection?.latestSuggestion;
  const languageDetectionSuggestion = latestLanguageDetectionSuggestion
    && latestLanguageDetectionSuggestion.sourceSnapshotId === detail?.currentSnapshot.id
    ? latestLanguageDetectionSuggestion
    : undefined;
  const languageDetectionStatus = (languageDetectionTask?.status
    ?? activeLanguageDetection?.status
    ?? detail?.languageDetection?.latestTaskStatus) as AdCreativeProviderSourceLanguageDetectionTaskStatus | undefined;
  const languageDetectionInProgress = Boolean(
    activeLanguageDetection
    && languageDetectionStatus
    && !TERMINAL_LANGUAGE_DETECTION_STATUSES.has(languageDetectionStatus),
  );
  const languageDetectionAttention = detail?.languageDetection?.attention;
  const languageDetectionRepairRequired = languageDetectionAttention?.kind === 'repair_required';
  const languageDetectionReplayBlocked = languageDetectionAttention?.kind === 'operator_reconciliation_required';
  const languageDetectionRepairPreflight = languageDetectionPreflight?.decision === 'ai_required'
    && languageDetectionPreflight.recoveryMode === 'restore_settled_candidate';
  const languageDetectionUiState = languageDetectionError
    ? 'error'
    : languageDetectionBusy === 'preflight'
      ? 'preflighting'
      : languageDetectionBusy === 'start'
        ? 'starting'
        : languageDetectionInProgress
          ? languageDetectionStatus
          : languageDetectionAttention
            ? languageDetectionAttention.kind
            : languageDetectionSuggestion
              ? 'result'
              : languageDetectionPreflight?.decision ?? 'idle';
  const exactLanguageConfirmed = Boolean(
    detail && sourceLocale && deploymentContext && baselineReady
      && isExactSnapshotLanguageConfirmed(
        detail,
        sourceLocale,
        deploymentContext,
        selectedBaselineEvidence?.id,
      ),
  );
  const availableTargetLocales = useMemo(
    () => projectLocales.filter((locale) => locale.code !== sourceLocale),
    [projectLocales, sourceLocale],
  );
  const deploymentSelectionRequired = Boolean(detail && !deploymentContext);

  return (
    <Stack spacing={2.5} data-testid={PROVIDER_SOURCES.page}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            data-testid={PROVIDER_SOURCES.back}
            onClick={onBack}
          >
            Create
          </Button>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
              Sync existing Meta creatives
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', whiteSpace: 'normal', overflowWrap: 'anywhere' }}
            >
              Build a reusable source library before creating Segmently variations.
            </Typography>
          </Box>
        </Stack>
        <Chip
          size="small"
          icon={<LockOutlinedIcon />}
          label="Meta · read-only"
          variant="outlined"
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        />
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Alert severity="info" icon={<LockOutlinedIcon />} data-testid={PROVIDER_SOURCES.truthNotice}>
        Import and refresh read Meta and store immutable source evidence; Segmently never edits Meta or runs AI during sync. Taxonomy
        analysis is a separate action below with its own estimate and confirmation. When media mirroring is enabled,
        Segmently stores the best provider source rendition Meta exposes; Meta does not guarantee that this is the
        original upload master.
      </Alert>

      {connected === false && (
        <Alert
          severity="warning"
          action={onOpenIntegrations ? (
            <Button color="inherit" size="small" onClick={onOpenIntegrations}>
              Open Integrations
            </Button>
          ) : undefined}
        >
          Meta is not connected. Existing synced sources remain available below; reconnect Meta in Integrations
          to read updates.
        </Alert>
      )}

      {connected && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
              <StyledTextField
                select
                size="small"
                label="Meta ad account"
                value={accountId}
                data-testid={PROVIDER_SOURCES.account}
                onChange={(value) => {
                  invalidateSourceSyncPreflight();
                  setAccountId(String(value));
                }}
                sx={{ minWidth: 280 }}
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>{account.name || account.id}</MenuItem>
                ))}
              </StyledTextField>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Bounded current metadata</Typography>
                <Typography variant="caption" color="text.secondary">
                  {allCurrentScope
                    ? 'Segmently will discover every accessible current campaign in each checked account and start one independently recoverable account shard under one visible job.'
                    : 'The preflight accepts up to 100 selected campaigns and reads only those campaigns and their ad sets and ads. Unrelated ads elsewhere in the account are not scanned.'}
                  {' '}{allCurrentScope ? 'Each account shard' : 'The selected scope'} is capped at 10,000 ads. Active and inactive ads are inspected so existing
                  {' '}creative evidence is not silently omitted. The media scope below separately controls which files
                  {' '}Segmently is allowed to mirror.
                </Typography>
              </Box>
            </Stack>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={allCurrentScope}
                      data-testid={PROVIDER_SOURCES.allCurrentScope}
                      onChange={(event) => {
                        setAllCurrentScope(event.target.checked);
                        invalidateSourceSyncPreflight();
                      }}
                    />
                  )}
                  label="Advanced: sync every current campaign from selected accounts"
                />
                {allCurrentScope && (
                  <Stack spacing={0.5} data-testid={PROVIDER_SOURCES.accountScope}>
                    <Typography variant="caption" color="text.secondary">
                      Select up to 10 connected accounts. A failed account never rolls back evidence committed by another account.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 0.5 }}>
                      {accounts.map((account) => (
                        <FormControlLabel
                          key={account.id}
                          control={(
                            <Checkbox
                              checked={selectedAccountIds.has(account.id)}
                              data-testid={PROVIDER_SOURCES.accountScopeCheckbox(account.id)}
                              onChange={() => {
                                setSelectedAccountIds((current) => {
                                  const next = new Set(current);
                                  if (next.has(account.id)) next.delete(account.id);
                                  else if (next.size < 10) next.add(account.id);
                                  return next;
                                });
                                invalidateSourceSyncPreflight();
                              }}
                            />
                          )}
                          label={account.name || account.id}
                        />
                      ))}
                    </Box>
                  </Stack>
                )}
              </Stack>
            </Paper>

            <Divider />

            {!allCurrentScope && <Stack spacing={1} data-testid={PROVIDER_SOURCES.campaignList}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Campaign scope</Typography>
                {campaignsLoading && <CircularProgress size={16} />}
                {!campaignsLoading && (
                  <Chip
                    size="small"
                    color={campaignInventoryComplete ? 'success' : 'warning'}
                    label={campaignInventoryComplete ? `${campaigns.length} fully loaded` : `${campaigns.length} loaded · more available`}
                  />
                )}
                {campaigns.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedCampaignIds(allCampaignsSelected
                        ? new Set()
                        : new Set(campaigns.map((campaign) => campaign.id)));
                      invalidateSourceSyncPreflight();
                    }}
                  >
                    {allCampaignsSelected ? 'Clear selection' : 'Select all'}
                  </Button>
                )}
              </Stack>
              {campaigns.length === 0 && !campaignsLoading && (
                <Typography variant="body2" color="text.secondary">
                  No campaign rows were returned. A selected-campaign preflight cannot start for this account.
                </Typography>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                {campaigns.map((campaign) => (
                  <Box
                    key={campaign.id}
                    data-testid={PROVIDER_SOURCES.campaign(campaign.id)}
                    sx={{ border: 1, borderColor: selectedCampaignIds.has(campaign.id) ? 'primary.main' : 'divider', borderRadius: 1, px: 1.25 }}
                  >
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={selectedCampaignIds.has(campaign.id)}
                          data-testid={PROVIDER_SOURCES.campaignCheckbox(campaign.id)}
                          onChange={() => toggleCampaign(campaign.id)}
                        />
                      )}
                      label={(
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{campaign.name || campaign.id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[campaign.effectiveStatus || campaign.configuredStatus, campaign.objective].filter(Boolean).join(' · ') || campaign.id}
                          </Typography>
                        </Box>
                      )}
                    />
                  </Box>
                ))}
              </Box>
              {campaignNextCursor && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={campaignsLoadingMore}
                  data-testid={PROVIDER_SOURCES.campaignLoadMore}
                  startIcon={campaignsLoadingMore ? <CircularProgress size={13} /> : undefined}
                  onClick={() => void loadMoreCampaigns()}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Load more campaigns
                </Button>
              )}
              {!campaignInventoryComplete && !campaignsLoading && (
                <Alert severity="warning">
                  Load every campaign page before reviewing the sync scope. Segmently will not treat a partial picker as the account inventory.
                </Alert>
              )}
              {campaignSubsetTooLarge && (
                <Alert severity="warning">
                  A preflight can include at most 100 explicitly selected campaigns. Reduce this selection to 100 campaigns.
                </Alert>
              )}
            </Stack>}

            <Paper variant="outlined" sx={{ p: 1.5 }} data-testid={PROVIDER_SOURCES.mediaScope}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Media scope</Typography>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={mirrorMedia}
                      data-testid={PROVIDER_SOURCES.mirrorMedia}
                      onChange={(event) => {
                        setMirrorMedia(event.target.checked);
                        if (!event.target.checked) setOnlySegmentlyAssociated(false);
                        invalidateSourceSyncPreflight();
                      }}
                    />
                  )}
                  label="Mirror available image and video renditions to Segmently storage"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={onlySegmentlyAssociated}
                      disabled={!mirrorMedia}
                      data-testid={PROVIDER_SOURCES.onlySegmentlyAssociated}
                      onChange={(event) => {
                        setOnlySegmentlyAssociated(event.target.checked);
                        invalidateSourceSyncPreflight();
                      }}
                    />
                  )}
                  label="Only fully sync ads associated with Segmently funnels"
                />
                {funnelAssociationScope === 'segmently_associated' && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <StyledTextField
                      type="date"
                      size="small"
                      label="From · inclusive"
                      value={associationFrom}
                      data-testid={PROVIDER_SOURCES.associationFrom}
                      onChange={(value) => {
                        setAssociationFrom(String(value));
                        invalidateSourceSyncPreflight();
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <StyledTextField
                      type="date"
                      size="small"
                      label="To · exclusive"
                      value={associationTo}
                      data-testid={PROVIDER_SOURCES.associationTo}
                      onChange={(value) => {
                        setAssociationTo(String(value));
                        invalidateSourceSyncPreflight();
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                )}
                {funnelAssociationScope === 'segmently_associated' && (
                  <Alert severity={associationWindowValid ? 'info' : 'error'}>
                    Full source sync includes an ad only when it has first-party Segmently attribution in this period
                    or its destination exactly matches a published project placement. No association in this period
                    and Unknown remain separate discovery evidence.
                  </Alert>
                )}
                {funnelAssociationScope === 'all' && (
                  <Alert severity="warning">
                    All selected ads are eligible for full source sync, including ads with no current Segmently-funnel association.
                  </Alert>
                )}
                {!mirrorMedia && (
                  <Alert severity="info">
                    Metadata, copy, CTA, destination and immutable provider references for all selected ads will sync.
                    The current metadata-only runner cannot enforce funnel-only filtering. No media resolver or file download will run.
                  </Alert>
                )}
                {mirrorMedia && (
                  <Alert severity="info">
                    For included sources, Segmently may mirror the best image and video renditions Meta exposes.
                    They are evidence copies, not claimed original-upload masters.
                  </Alert>
                )}
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <Button
                variant="outlined"
                startIcon={preflighting ? <CircularProgress size={15} /> : <AutorenewIcon />}
                disabled={!canPreflight}
                data-testid={PROVIDER_SOURCES.preflight}
                onClick={() => void runPreflight()}
              >
                Review exact sync scope
              </Button>
              <Typography variant="caption" color="text.secondary">
                {allCurrentScope
                  ? `${selectedAccountIds.size} account${selectedAccountIds.size === 1 ? '' : 's'} selected for whole-account discovery`
                  : campaigns.length > 0
                  ? `${selectedCampaignCount} of ${campaigns.length} campaigns selected`
                  : 'No campaigns available for a selected-campaign preflight'}
              </Typography>
            </Stack>

            {sourceSyncPreflight && (
              <Paper
                variant="outlined"
                sx={{ p: 1.5 }}
                data-testid={PROVIDER_SOURCES.preflightResult}
                aria-label="Meta creative source sync preflight result"
                aria-live="polite"
              >
                <Stack spacing={1.25}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                      Exact server-authorized scope
                    </Typography>
                    <Chip
                      size="small"
                      color={sourceSyncPreflight.freshnessState === 'fresh' ? 'success' : 'warning'}
                      label={`${sourceSyncPreflight.freshnessState} · ${sourceSyncPreflight.selectionSource.replaceAll('_', ' ')}`}
                    />
                  </Stack>
                  {sourceSyncPreflight.coordination && (
                    <Alert severity="info">
                      {`${sourceSyncPreflight.coordination.accountCount} accounts reviewed · ${sourceSyncPreflight.coordination.childRunCount} independently recoverable account shards will start under one job.`}
                    </Alert>
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    <Box component="span" data-testid={PROVIDER_SOURCES.preflightInspectedAds}>
                      {`${sourceSyncPreflight.totals.ads} ads inspected`}
                    </Box>
                    {' → '}
                    <Box component="span" data-testid={PROVIDER_SOURCES.preflightIncludedBindings}>
                      {`${sourceSyncPreflight.totals.includedBindings} bindings included`}
                    </Box>
                    {' → '}
                    <Box component="span" data-testid={PROVIDER_SOURCES.preflightIncludedUniqueCreatives}>
                      {`${sourceSyncPreflight.totals.includedUniqueCreativeIds} unique creatives included`}
                    </Box>
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      variant="outlined"
                      data-testid={PROVIDER_SOURCES.preflightNotLinked}
                      label={`No association in this period: ${sourceSyncPreflight.funnelAssociation.ads.notLinked}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      data-testid={PROVIDER_SOURCES.preflightUnknown}
                      label={`Unknown: ${sourceSyncPreflight.funnelAssociation.ads.unknown}`}
                    />
                    <Chip size="small" variant="outlined" label={`Already synced: ${sourceSyncPreflight.totals.alreadySynced}`} />
                  </Stack>
                  <Box
                    role="list"
                    aria-label="Campaign preflight breakdown"
                    sx={{ display: 'grid', gap: 0.75, maxHeight: 220, overflowY: 'auto' }}
                  >
                    {sourceSyncPreflight.accounts.flatMap((account) => account.campaigns.map((campaign) => (
                      <Box
                        key={`${account.accountId}:${campaign.campaignId}`}
                        role="listitem"
                        data-testid={PROVIDER_SOURCES.preflightCampaign(campaign.campaignId)}
                        sx={{ px: 1, py: 0.75, border: 1, borderColor: 'divider', borderRadius: 1 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {campaign.campaignName || campaign.campaignId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`${campaign.ads} inspected → ${campaign.includedBindings} bindings → ${campaign.includedUniqueCreativeIds} unique creatives`}
                          {` · No association in this period: ${campaign.notLinked} · Unknown: ${campaign.unknown}`}
                        </Typography>
                      </Box>
                    )))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" data-testid={PROVIDER_SOURCES.preflightExpiry}>
                    {`Source as of ${formatDate(sourceSyncPreflight.sourceAsOf)} · `}
                    {preflightExpired
                      ? `Preflight expired ${formatDate(sourceSyncPreflight.expiresAt)}. Review the scope again.`
                      : `Preflight expires ${formatDate(sourceSyncPreflight.expiresAt)}.`}
                  </Typography>
                  {sourceSyncPreflight.warnings.map((warning) => (
                    <Alert
                      key={`${warning.code}:${warning.accountId ?? ''}`}
                      severity="warning"
                      data-testid={PROVIDER_SOURCES.preflightWarning(warning.code)}
                    >
                      {warning.message}
                    </Alert>
                  ))}
                  {emptySelectedPreflight && (
                    <Alert
                      severity="warning"
                      data-testid={PROVIDER_SOURCES.preflightEmptySelectedScope}
                    >
                      No ads were found in the selected campaigns, including inactive ads. Select different campaigns
                      above, then review a fresh sync scope before starting.
                    </Alert>
                  )}
                  {zeroAssociatedPreflight && (
                    <Alert severity="warning">
                      <Stack spacing={1} alignItems="flex-start">
                        <Typography variant="body2">
                          No selected ads are associated with Segmently funnels in this period. Sync cannot start with this empty included scope.
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          data-testid={PROVIDER_SOURCES.includeAllSelectedAds}
                          onClick={() => {
                            setOnlySegmentlyAssociated(false);
                            invalidateSourceSyncPreflight();
                          }}
                        >
                          Include all selected ads
                        </Button>
                      </Stack>
                    </Alert>
                  )}
                </Stack>
              </Paper>
            )}

            {sourceSyncPreflight && (
              <FormControlLabel
                control={(
                    <Checkbox
                      checked={syncConfirmed}
                      disabled={preflightExpired || emptySelectedPreflight || zeroAssociatedPreflight}
                    data-testid={PROVIDER_SOURCES.confirmation}
                    onChange={(event) => setSyncConfirmed(event.target.checked)}
                  />
                )}
                label="I confirm this exact preflight scope. Segmently will store its own evidence copies, run no AI, and never change Meta ads."
              />
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <Button
                variant="contained"
                startIcon={syncing ? <CircularProgress size={15} /> : <AutorenewIcon />}
                disabled={!canSync}
                data-testid={PROVIDER_SOURCES.sync}
                onClick={() => void runSync()}
              >
                {sources.length > 0 ? 'Sync creative updates' : 'Import current creatives'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Start uses only the current short-lived preflight token; campaign, date, association, and media fields are not resubmitted.
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      )}

      {syncResult && (
        <Alert
          severity={syncResult.status === 'completed' && syncResult.outcome === 'complete'
            ? 'success'
            : ['pending', 'running', 'cancelling'].includes(syncResult.status) ? 'info' : 'warning'}
          data-testid={PROVIDER_SOURCES.syncResult}
        >
          <Stack spacing={0.75}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {syncResult.status === 'completed'
                ? syncResult.outcome === 'partial_success'
                  ? 'Source sync finished with usable partial results.'
                  : syncResult.outcome === 'empty'
                    ? 'Source sync finished with no matching creatives.'
                    : 'Source sync complete.'
                : syncResult.status === 'needs_reauth'
                  ? 'Source sync paused until Meta is reconnected.'
                  : syncResult.status === 'paused_quota'
                    ? 'Source sync paused at the media quota checkpoint.'
                    : syncResult.status === 'retrying'
                      ? 'Source sync paused after a provider rate limit.'
                      : syncResult.status === 'cancelled'
                        ? 'Source sync cancelled after its last durable checkpoint.'
                        : `Source sync is running: ${syncResult.phase.replaceAll('_', ' ')}.`}
            </Typography>
            <Typography variant="body2">
              {`${syncResult.progress.counters.adsDiscovered} ads inspected, `}
              {`${syncResult.progress.counters.sourcesCommitted} canonical sources committed, `}
              {`${syncResult.progress.counters.snapshotsCreated} immutable snapshots added, `}
              {`${syncResult.progress.counters.bindingsCommitted} full bindings written, and `}
              {`${syncResult.progress.counters.lightweightCandidatesPersisted} lightweight candidates retained.`}
            </Typography>
            <Typography variant="caption">
              {`${syncResult.progress.creativeShardsCompleted} of ${syncResult.progress.creativeShardsTotal} creative shards complete · `}
              {`${syncResult.progress.pagesRead.ads} Meta ad pages checkpointed. No AI started and no Meta object was changed.`}
            </Typography>
            {syncResult.attention && (
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{syncResult.attention.message}</Typography>
            )}
            {(syncResult.canCancel || syncResult.canResume) && (
              <Stack direction="row" spacing={1}>
                {syncResult.canCancel && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={syncing}
                    data-testid={PROVIDER_SOURCES.syncCancel}
                    onClick={() => void cancelSync()}
                  >
                    Cancel after checkpoint
                  </Button>
                )}
                {syncResult.canResume && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={syncing}
                    data-testid={PROVIDER_SOURCES.syncResume}
                    onClick={() => void resumeSync()}
                  >
                    Resume from checkpoint
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </Alert>
      )}

      {(candidates.length > 0 || candidatesLoading) && connected && accountId && (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }} data-testid={PROVIDER_SOURCES.candidateQueue}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Discovery candidates</Typography>
                <Typography variant="body2" color="text.secondary">
                  These ads were discovered without downloading their creative definitions or media. Select only the ones you want to sync now.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={`${candidates.length} loaded`} />
                {candidatesLoading && <CircularProgress size={16} />}
                <Button
                  size="small"
                  variant="text"
                  disabled={candidates.length === 0 || syncInProgress}
                  onClick={() => {
                    setSelectedCandidateIds(new Set(candidates.slice(0, 100).map((candidate) => candidate.id)));
                    setCandidatePreflight(null);
                    setCandidatePreflightIds([]);
                    setCandidateConfirmed(false);
                  }}
                >
                  Select loaded
                </Button>
              </Stack>
            </Stack>

            <Box role="list" aria-label="Discovery candidates" sx={{ display: 'grid', gap: 1 }}>
              {candidates.map((candidate) => (
                <Paper
                  key={candidate.id}
                  role="listitem"
                  variant="outlined"
                  data-testid={PROVIDER_SOURCES.candidate(candidate.id)}
                  sx={{ px: 1.5, py: 1.25 }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Checkbox
                      size="small"
                      checked={selectedCandidateIds.has(candidate.id)}
                      disabled={syncInProgress}
                      inputProps={{ 'aria-label': `Select ${candidate.adName || candidate.providerAdId}` }}
                      data-testid={PROVIDER_SOURCES.candidateCheckbox(candidate.id)}
                      onChange={() => toggleCandidate(candidate.id)}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {candidate.adName || `Meta ad ${candidate.providerAdId}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${candidate.campaignName || candidate.campaignId || 'Campaign unavailable'}`}
                        {candidate.adSetName ? ` · ${candidate.adSetName}` : ''}
                        {` · observed ${formatDate(candidate.latestObservedAt)}`}
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                        <Chip
                          size="small"
                          color={candidate.funnelAssociation.state === 'unknown' ? 'warning' : 'default'}
                          variant="outlined"
                          label={candidateAssociationLabel(candidate.funnelAssociation.state)}
                        />
                        {candidate.effectiveStatus && <Chip size="small" variant="outlined" label={candidate.effectiveStatus} />}
                        {candidateDestinationHosts(candidate).map((host) => (
                          <Chip key={host} size="small" variant="outlined" label={host} />
                        ))}
                      </Stack>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      No media yet
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Box>

            {candidateNextCursor && (
              <Button
                variant="outlined"
                disabled={candidatesLoadingMore}
                data-testid={PROVIDER_SOURCES.candidateLoadMore}
                onClick={() => void loadMoreCandidates()}
              >
                {candidatesLoadingMore ? 'Loading candidates…' : 'Load more candidates'}
              </Button>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Button
                variant="outlined"
                startIcon={candidatePreflighting ? <CircularProgress size={15} /> : <AutorenewIcon />}
                disabled={!canPreflightCandidates}
                data-testid={PROVIDER_SOURCES.candidatePreflight}
                onClick={() => void runCandidatePreflight()}
              >
                Review selected sync
              </Button>
              <Typography variant="caption" color="text.secondary">
                {`${selectedCandidateIds.size} selected · maximum 100 per exact read-only sync.`}
              </Typography>
            </Stack>

            {candidatePreflight && (
              <Paper variant="outlined" sx={{ p: 1.5 }} data-testid={PROVIDER_SOURCES.candidatePreflightResult}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {`${candidatePreflight.totals.ads} exact Meta ${candidatePreflight.totals.ads === 1 ? 'ad' : 'ads'} reauthorized for full source and media sync`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {`Association was refreshed for ${candidatePreflight.funnelAssociation.evaluatedFrom} to ${candidatePreflight.funnelAssociation.evaluatedTo}: `}
                    {`${candidatePreflight.funnelAssociation.ads.linkedInWindow} linked, `}
                    {`${candidatePreflight.funnelAssociation.ads.notLinked} with no association in this period, and `}
                    {`${candidatePreflight.funnelAssociation.ads.unknown} unknown. All selected ads remain included because you selected them explicitly.`}
                  </Typography>
                  {candidatePreflightExpired && (
                    <Alert severity="warning">This review expired. Review the selected candidates again before starting.</Alert>
                  )}
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={candidateConfirmed}
                        disabled={candidatePreflightExpired}
                        data-testid={PROVIDER_SOURCES.candidateConfirmation}
                        onChange={(event) => setCandidateConfirmed(event.target.checked)}
                      />
                    )}
                    label="I confirm these exact ads. Segmently will download its own evidence copies and will not edit anything in Meta."
                  />
                  <Button
                    variant="contained"
                    disabled={!canSyncCandidates}
                    data-testid={PROVIDER_SOURCES.candidateSync}
                    onClick={() => void runCandidateSync()}
                  >
                    Sync selected candidates
                  </Button>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Paper>
      )}

      <Stack spacing={1.25} data-testid={PROVIDER_SOURCES.sourceList}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Synced source evidence</Typography>
              <Chip size="small" label={`${sources.length} loaded`} />
              {sourcesLoading && <CircularProgress size={16} />}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Select up to 20 exact snapshots for a separate AI taxonomy review. Sync itself never runs AI.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={taxonomyBusy === 'preflight' ? <CircularProgress size={14} /> : undefined}
            disabled={selectedSourceIds.size < 1 || selectedSourceIds.size > 20 || Boolean(taxonomyBusy) || Boolean(activeTaxonomyTaskId)}
            data-testid={PROVIDER_SOURCES.taxonomyPreflight}
            onClick={() => void preflightTaxonomy()}
          >
            Analyze selected
          </Button>
        </Stack>
        {activeTaxonomyTaskId && (
          <Alert severity="info" data-testid={PROVIDER_SOURCES.taxonomyTaskState}>
            Taxonomy analysis is running in the background. Each result remains a review-required Segmently suggestion; Meta is unchanged.
          </Alert>
        )}
        {taxonomyPreflight && (
          <Paper variant="outlined" sx={{ p: 1.5 }} data-testid={PROVIDER_SOURCES.taxonomyPreflightResult}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {`${taxonomyPreflight.sourceCount} exact creative ${taxonomyPreflight.sourceCount === 1 ? 'snapshot' : 'snapshots'} · ${taxonomyPreflight.model}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`Estimated $${taxonomyPreflight.costEstimate.estimatedCostUsd.toFixed(4)}. The output suggests topic, angle, concept, hook, proof mechanism, and format. It does not infer performance and requires review.`}
              </Typography>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={taxonomyConfirmed}
                    data-testid={PROVIDER_SOURCES.taxonomyConfirmation}
                    onChange={(event) => setTaxonomyConfirmed(event.target.checked)}
                  />
                )}
                label="I approve this exact AI analysis estimate"
              />
              <Button
                variant="contained"
                disabled={!taxonomyConfirmed || Boolean(taxonomyBusy)}
                data-testid={PROVIDER_SOURCES.taxonomyStart}
                onClick={() => void startTaxonomy()}
              >
                Start taxonomy analysis
              </Button>
            </Stack>
          </Paper>
        )}
        {sources.length === 0 && !sourcesLoading && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>No provider sources synced yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {connected === false
                ? 'Reconnect Meta in Integrations, then return here to choose an account and campaign scope.'
                : 'Choose an account and campaign scope above. From-scratch creation remains available from Create.'}
            </Typography>
          </Paper>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
          {sources.map((source) => {
            const state = providerSourceLanguageUiState(source.sourceLanguage);
            return (
              <Paper key={source.id} variant="outlined" data-testid={PROVIDER_SOURCES.sourceCard(source.id)} sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Checkbox
                      checked={selectedSourceIds.has(source.id)}
                      disabled={!selectedSourceIds.has(source.id) && selectedSourceIds.size >= 20}
                      inputProps={{ 'aria-label': `Select ${source.creativeName || source.providerCreativeId} for taxonomy analysis` }}
                      data-testid={PROVIDER_SOURCES.sourceCheckbox(source.id)}
                      onChange={(event) => {
                        setSelectedSourceIds((current) => {
                          const next = new Set(current);
                          if (event.target.checked) next.add(source.id);
                          else next.delete(source.id);
                          return next;
                        });
                        setTaxonomyPreflight(null);
                        setTaxonomyConfirmed(false);
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap title={source.creativeName || source.providerCreativeId}>
                        {source.creativeName || `Meta creative ${source.providerCreativeId}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`Meta creative ${source.providerCreativeId} · synced ${formatDate(source.lastSyncedAt)}`}
                      </Typography>
                    </Box>
                    <Chip size="small" variant="outlined" label="immutable source" />
                  </Stack>
                  {(source.primaryText || source.headline) && (
                    <Typography variant="body2">
                      {source.primaryText || source.headline}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`${languageStateLabel(state)}${source.sourceLanguage.locale ? ` · ${source.sourceLanguage.locale}` : ''}`} />
                    <Chip size="small" label={`${source.mediaSummary.mediaCount} media refs · ${source.mediaSummary.mirrorState.replaceAll('_', ' ')}`} />
                    <Chip size="small" label={`Used by ${source.deploymentCount} ads / ${source.campaignCount} campaigns`} />
                    <Chip
                      size="small"
                      color={source.taxonomyState === 'reviewed' ? 'success' : source.taxonomyState === 'stale' ? 'warning' : 'default'}
                      label={taxonomyStateLabel(source.taxonomyState)}
                    />
                    {source.presentationState !== 'exact_static' && <Chip size="small" color="warning" label={source.presentationState.replaceAll('_', ' ')} />}
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {source.mediaSummary.mirrorState === 'provider_reference_only'
                        ? 'Metadata only · source files not mirrored'
                        : 'Segmently media copy available · Meta source unchanged'}
                    </Typography>
                    <Button
                      size="small"
                      data-testid={PROVIDER_SOURCES.sourceOpen(source.id)}
                      onClick={() => void openSource(source.id)}
                    >
                      View source
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Box>
        {sourceNextCursor && (
          <Stack alignItems="center" sx={{ pt: 0.5 }}>
            <Button
              variant="outlined"
              disabled={sourcesLoadingMore}
              data-testid={PROVIDER_SOURCES.sourceLoadMore}
              onClick={() => void loadSources(accountId || undefined, sourceNextCursor)}
            >
              {sourcesLoadingMore ? 'Loading more…' : 'Load more synced sources'}
            </Button>
          </Stack>
        )}
      </Stack>

      <Dialog
        open={Boolean(selectedSourceId)}
        onClose={closeDetail}
        fullWidth
        maxWidth="lg"
        data-testid={PROVIDER_SOURCES.detail}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {detail?.source.creativeName || 'Meta creative source'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {detail ? `Exact immutable snapshot · ${formatDate(detail.currentSnapshot.sourceAsOf)}` : 'Loading immutable evidence…'}
              </Typography>
            </Box>
            <Chip size="small" icon={<LockOutlinedIcon />} variant="outlined" label="Meta source · read-only" />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {detailLoading && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
          {detail && (
            <Stack spacing={2.5}>
              <Alert severity="info">
                The source and snapshot stay unchanged. The handoff below creates a separate Segmently-owned copy
                with locale-keyed content. No AI starts automatically; the optional detector below has its own
                estimate and confirmation. Asset generation, publishing, and Meta changes never start here.
              </Alert>

              <ProviderSourceMediaGallery mediaRefs={detail.currentSnapshot.mediaRefs} />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.25fr) minmax(280px, .75fr)' }, gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">Provider copy & CTA</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {detail.currentSnapshot.displayProjection.headline || 'No headline returned'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                    {detail.currentSnapshot.displayProjection.primaryText || 'No primary text returned'}
                  </Typography>
                  {detail.currentSnapshot.displayProjection.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {detail.currentSnapshot.displayProjection.description}
                    </Typography>
                  )}
                  <Divider sx={{ my: 1.5 }} />
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`CTA type: ${detail.currentSnapshot.displayProjection.ctaType || 'not returned'}`} />
                    <Chip size="small" label={`${detail.currentSnapshot.displayProjection.primaryTextCandidates.length} body candidates`} />
                    <Chip size="small" label={`${detail.currentSnapshot.displayProjection.headlineCandidates.length} headline candidates`} />
                  </Stack>
                  {detail.currentSnapshot.displayProjection.ctaType && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      The Meta CTA enum is kept as source lineage. It is not copied as a universal localized button label.
                    </Typography>
                  )}
                  {detail.currentSnapshot.displayProjection.destinationUrl && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, overflowWrap: 'anywhere' }}>
                      {`Destination evidence: ${detail.currentSnapshot.displayProjection.destinationUrl}`}
                    </Typography>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">Capability</Typography>
                  <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                    <Typography variant="body2">{`${detail.currentSnapshot.mediaRefs.length} provider media references`}</Typography>
                    <Typography variant="body2">{`${detail.source.mediaSummary.videoCount} video references`}</Typography>
                    <Typography variant="body2">{`${detail.source.mediaSummary.carouselCardCount} carousel cards`}</Typography>
                    <Typography variant="body2">{`Assembly: ${detail.source.presentationState.replaceAll('_', ' ')}`}</Typography>
                  </Stack>
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    These are not mirrored originals. Meta may expose processed renditions or candidate sets, and this
                    foundation makes no original-quality claim.
                  </Alert>
                </Paper>
              </Box>

              <Paper variant="outlined" sx={{ p: 2 }} data-testid={PROVIDER_SOURCES.taxonomyDetail}>
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Creative taxonomy</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Topic, angle, concept, hook, proof mechanism, and format are Segmently review evidence. They never change the Meta creative.
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={taxonomyCurrent?.state === 'reviewed' ? 'success' : taxonomyCurrent?.state === 'stale' ? 'warning' : 'default'}
                      label={taxonomyStateLabel(taxonomyCurrent?.state ?? detail.source.taxonomyState)}
                    />
                  </Stack>

                  {!taxonomyCurrent?.taxonomy && (
                    <Alert severity={taxonomyCurrent?.state === 'stale' ? 'warning' : 'info'}>
                      {taxonomyCurrent?.state === 'stale'
                        ? 'The imported creative changed after the previous taxonomy snapshot. Select this source in the library and run a new analysis.'
                        : 'No taxonomy suggestion exists for this exact snapshot. Close this dialog, select the source, and review an analysis estimate.'}
                    </Alert>
                  )}

                  {taxonomyCurrent?.taxonomy && taxonomyDraft && (
                    <>
                      {taxonomyCurrent.taxonomy.state === 'suggested' && (
                        <Alert severity="warning">
                          AI analyzed only the displayed copy and bounded presentation facts. Review the classifications and rationale below before approving them for reuse.
                        </Alert>
                      )}
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                        <StyledTextField
                          label="Topic"
                          value={taxonomyDraft.topic.label}
                          disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                          onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, topic: { ...current.topic, label: String(value) } } : current)}
                        />
                        <StyledTextField
                          label="Angle"
                          value={taxonomyDraft.angle.label}
                          disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                          onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, angle: { ...current.angle, label: String(value) } } : current)}
                        />
                        <StyledTextField
                          label="Concept"
                          value={taxonomyDraft.concept.label}
                          disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                          onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, concept: { ...current.concept, label: String(value) } } : current)}
                        />
                        <StyledTextField
                          label="Hook"
                          value={taxonomyDraft.hook.label}
                          disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                          onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, hook: { ...current.hook, label: String(value) } } : current)}
                        />
                        <StyledTextField
                          label="Proof mechanism"
                          value={taxonomyDraft.proof.strategy}
                          disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                          onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, proof: { ...current.proof, strategy: String(value) } } : current)}
                        />
                        <StyledTextField
                          label="Format"
                          value={taxonomyDraft.format.family}
                          disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                          onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, format: { ...current.format, family: String(value) } } : current)}
                        />
                      </Box>
                      <StyledTextField
                        label="Topic summary"
                        value={taxonomyDraft.topic.summary}
                        multiline
                        minRows={2}
                        disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                        onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, topic: { ...current.topic, summary: String(value) } } : current)}
                      />
                      <StyledTextField
                        label="Angle thesis"
                        value={taxonomyDraft.angle.thesis}
                        multiline
                        minRows={2}
                        disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                        onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, angle: { ...current.angle, thesis: String(value) } } : current)}
                      />
                      <StyledTextField
                        label="Concept description"
                        value={taxonomyDraft.concept.description}
                        multiline
                        minRows={2}
                        disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                        onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, concept: { ...current.concept, description: String(value) } } : current)}
                      />
                      <StyledTextField
                        label="Hook pattern"
                        value={taxonomyDraft.hook.pattern}
                        multiline
                        minRows={2}
                        disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                        onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, hook: { ...current.hook, pattern: String(value) } } : current)}
                      />
                      <StyledTextField
                        label="Proof caution"
                        value={taxonomyDraft.proof.caution}
                        multiline
                        minRows={2}
                        disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                        onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, proof: { ...current.proof, caution: String(value) } } : current)}
                      />
                      <StyledTextField
                        label="Format rationale"
                        value={taxonomyDraft.format.rationale}
                        multiline
                        minRows={2}
                        disabled={taxonomyCurrent.taxonomy.state !== 'suggested'}
                        onChange={(value) => setTaxonomyDraft((current) => current ? { ...current, format: { ...current.format, rationale: String(value) } } : current)}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {`AI confidence ${Math.round(taxonomyDraft.confidence * 100)}% · cited source fields: ${taxonomyCurrent.taxonomy.evidenceEntries
                          .filter((entry) => taxonomyDraft.proof.evidenceEntryKeys.includes(entry.entryKey))
                          .map((entry) => entry.field)
                          .join(', ') || 'none'}${taxonomyDraft.warnings.length ? ` · warnings: ${taxonomyDraft.warnings.join('; ')}` : ''}`}
                      </Typography>
                      {taxonomyCurrent.taxonomy.state === 'suggested' && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <Button
                            variant="contained"
                            disabled={Boolean(taxonomyBusy)}
                            data-testid={PROVIDER_SOURCES.taxonomyApprove}
                            onClick={() => void reviewTaxonomy('approve')}
                          >
                            Approve reviewed taxonomy
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            disabled={Boolean(taxonomyBusy)}
                            data-testid={PROVIDER_SOURCES.taxonomyReject}
                            onClick={() => void reviewTaxonomy('reject')}
                          >
                            Reject suggestion
                          </Button>
                        </Stack>
                      )}
                    </>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>1. Choose the Meta ad, baseline, and source language</Typography>
                      <Chip
                        size="small"
                        data-testid={PROVIDER_SOURCES.languageState}
                        color={languageState === 'known' ? 'success' : languageState === 'suggested' ? 'warning' : 'default'}
                        label={`${languageStateLabel(languageState)}${detail.source.sourceLanguage.locale ? ` · ${detail.source.sourceLanguage.locale}` : ''}`}
                      />
                      {detail.source.sourceLanguage.confidence != null && (
                        <Chip size="small" variant="outlined" label={`${Math.round(detail.source.sourceLanguage.confidence * 100)}% confidence`} />
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Provider declarations are known evidence; AI or heuristic results are suggestions. We still ask
                      you to confirm the language against this exact snapshot and Meta ad binding before creating an editable copy.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5 }}>Meta ad binding</Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                      {detail.bindings.map((binding) => (
                        <Chip
                          key={binding.id}
                          clickable
                          color={deploymentContext === binding.id ? 'primary' : 'default'}
                          variant={deploymentContext === binding.id ? 'filled' : 'outlined'}
                          label={`${binding.campaignName || binding.campaignId || 'Campaign'} · ${binding.adName || binding.providerAdId}`}
                          data-testid={PROVIDER_SOURCES.deploymentContext(binding.id)}
                          onClick={() => {
                            setDeploymentContext(binding.id);
                            setSelectedBaselineCandidateId('');
                            setCreatedDraft(null);
                          }}
                        />
                      ))}
                      {detail.bindingsPage.hasMore && (
                        <Button
                          size="small"
                          variant="text"
                          disabled={bindingsLoadingMore || baselineBusy || languageBusy || draftBusy}
                          startIcon={bindingsLoadingMore ? <CircularProgress size={13} /> : undefined}
                          onClick={() => void loadMoreBindings()}
                        >
                          Load more Meta ads
                        </Button>
                      )}
                    </Stack>
                    {detail.bindings.length === 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        This source has no exact Meta ad binding, so a Segmently copy cannot be created yet.
                      </Alert>
                    )}
                    {(detail.bindings.length > 1 || detail.bindingsPage.hasMore) && !deploymentContext && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Select the exact Meta ad first. Language confirmations are isolated per ad even when the creative is shared.
                      </Alert>
                    )}
                    {baselineRequired && (
                      <Stack spacing={1} sx={{ mt: 2 }} data-testid={PROVIDER_SOURCES.baselineChoices}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Provider candidate or rendition baseline</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Choose one candidate Meta actually returned. Segmently freezes that exact choice for this ad;
                          it never invents combinations across independent dynamic asset arrays.
                        </Typography>
                        {detail.baselineChoices.length === 0 && (
                          <Alert severity="warning">
                            Meta did not expose a provider-real candidate with a durable Segmently-hosted rendition.
                            This source cannot create a variation safely yet.
                          </Alert>
                        )}
                        {detail.baselineChoices.map((choice) => (
                          <Button
                            key={choice.id}
                            variant={selectedBaselineCandidateId === choice.id ? 'contained' : 'outlined'}
                            color={choice.eligible ? 'primary' : 'inherit'}
                            disabled={!deploymentContext || !choice.eligible || baselineBusy}
                            data-testid={PROVIDER_SOURCES.baselineChoice(choice.id)}
                            onClick={() => {
                              setSelectedBaselineCandidateId(choice.id);
                              setCreatedDraft(null);
                            }}
                            sx={{
                              justifyContent: 'stretch',
                              textTransform: 'none',
                              textAlign: 'left',
                              p: 1.25,
                            }}
                          >
                            <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{choice.label}</Typography>
                                <Chip size="small" label={choice.kind.replaceAll('_', ' ')} />
                                {choice.cards && <Chip size="small" label={`${choice.cards.length} ordered cards`} />}
                              </Stack>
                              <ProviderBaselineMediaPreview choice={choice} />
                              {(choice.copy.primaryText || choice.copy.headline || choice.copy.description) && (
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {[choice.copy.primaryText, choice.copy.headline, choice.copy.description].filter(Boolean).join(' · ')}
                                </Typography>
                              )}
                              {choice.blockingReasons.map((reason) => (
                                <Typography key={reason} variant="caption" color="error.main">{reason}</Typography>
                              ))}
                            </Stack>
                          </Button>
                        ))}
                        <Button
                          variant={selectedBaselineEvidence ? 'outlined' : 'contained'}
                          startIcon={baselineBusy ? <CircularProgress size={14} /> : <LockOutlinedIcon />}
                          disabled={!deploymentContext || !selectedBaselineChoice?.eligible || baselineBusy || Boolean(selectedBaselineEvidence)}
                          data-testid={PROVIDER_SOURCES.selectBaseline}
                          onClick={() => void freezeBaseline()}
                        >
                          {selectedBaselineEvidence ? 'Baseline frozen for this Meta ad' : 'Freeze selected baseline'}
                        </Button>
                        {selectedBaselineEvidence && (
                          <Alert severity="success" data-testid={PROVIDER_SOURCES.baselineSelected}>
                            This exact provider candidate/rendition is frozen as immutable Segmently evidence for the selected Meta ad.
                          </Alert>
                        )}
                      </Stack>
                    )}
                  </Box>
                  <Stack sx={{ minWidth: { md: 340 } }} spacing={1}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5 }}
                      data-testid={PROVIDER_SOURCES.languageDetectionState}
                      data-state={languageDetectionUiState}
                    >
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Detector suggestion</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Optional and separate from your manual confirmation.
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={languageDetectionBusy === 'preflight' ? <CircularProgress size={13} /> : <LanguageIcon />}
                            disabled={Boolean(languageDetectionBusy) || languageDetectionInProgress || languageDetectionReplayBlocked}
                            data-testid={PROVIDER_SOURCES.detectLanguage}
                            onClick={() => void preflightLanguageDetection()}
                          >
                            {languageDetectionBusy === 'preflight'
                              ? 'Checking…'
                              : languageDetectionRepairRequired
                                ? 'Restore saved result'
                                : languageDetectionReplayBlocked
                                  ? 'Automatic replay blocked'
                                  : 'Detect language'}
                          </Button>
                        </Stack>

                        {languageDetectionError && (
                          <Alert
                            severity="error"
                            data-testid={PROVIDER_SOURCES.languageDetectionError}
                            onClose={() => setLanguageDetectionError(null)}
                          >
                            {languageDetectionError}
                          </Alert>
                        )}

                        {languageDetectionAttention && (
                          <Alert
                            severity={languageDetectionAttention.kind === 'repair_required' ? 'warning' : 'error'}
                            data-testid={PROVIDER_SOURCES.languageDetectionAttention}
                          >
                            {languageDetectionAttentionMessage(languageDetectionAttention)}
                          </Alert>
                        )}

                        {languageDetectionPreflight?.decision === 'provider_known' && (
                          <Alert severity="info" data-testid={PROVIDER_SOURCES.languageDetectionPreflight}>
                            {`Meta already provides authoritative language evidence${languageDetectionPreflight.providerLocale ? ` (${languageDetectionPreflight.providerLocale})` : ''}. AI is not needed; confirm the project locale manually below.`}
                          </Alert>
                        )}

                        {languageDetectionPreflight?.decision === 'insufficient_text' && (
                          <Alert severity="warning" data-testid={PROVIDER_SOURCES.languageDetectionPreflight}>
                            {`There is not enough eligible ad copy to detect language safely (${languageDetectionPreflight.inputCharacterCount} characters). Nothing was billed or selected.`}
                          </Alert>
                        )}

                        {languageDetectionPreflight?.decision === 'mixed' && (
                          <Alert severity="warning" data-testid={PROVIDER_SOURCES.languageDetectionPreflight}>
                            The eligible copy contains mixed-language evidence. Detection stays unknown and no project locale was selected.
                          </Alert>
                        )}

                        {languageDetectionPreflight?.decision === 'ai_required' && !languageDetectionInProgress && (
                          <Stack spacing={1}>
                            <Alert severity="info" data-testid={PROVIDER_SOURCES.languageDetectionEstimate}>
                              {languageDetectionRepairPreflight
                                ? 'The settled detector result is ready to restore. This repair makes no new provider call and has no new billing charge.'
                                : `${languageDetectionPreflight.inputCharacterCount} eligible characters · ${languageDetectionPreflight.model} · estimated $${languageDetectionPreflight.costEstimate.estimatedCostUsd.toFixed(4)}. This only creates a suggestion.`}
                            </Alert>
                            <FormControlLabel
                              data-testid={PROVIDER_SOURCES.languageDetectionConfirmation}
                              control={(
                                <Checkbox
                                  checked={languageDetectionConfirmed}
                                  onChange={(event) => setLanguageDetectionConfirmed(event.target.checked)}
                                />
                              )}
                              label={languageDetectionRepairPreflight
                                ? 'I approve restoring the saved result'
                                : 'I approve this detection estimate'}
                            />
                            <Button
                              variant="contained"
                              startIcon={languageDetectionBusy === 'start' ? <CircularProgress size={14} /> : <LanguageIcon />}
                              disabled={!languageDetectionConfirmed || Boolean(languageDetectionBusy)}
                              data-testid={PROVIDER_SOURCES.languageDetectionStart}
                              onClick={() => void startLanguageDetection()}
                            >
                              {languageDetectionBusy === 'start'
                                ? languageDetectionRepairPreflight ? 'Restoring…' : 'Starting…'
                                : languageDetectionRepairPreflight ? 'Restore saved result' : 'Start detection'}
                            </Button>
                          </Stack>
                        )}

                        {languageDetectionInProgress && (
                          <Alert severity={languageDetectionStatus === 'retrying' ? 'warning' : 'info'}>
                            {languageDetectionStatus === 'retrying'
                              ? 'Language detection is retrying. Its task keeps the same exact snapshot.'
                              : 'Language detection is running in the background. You may keep reviewing the source.'}
                          </Alert>
                        )}

                        {languageDetectionStatus === 'completed'
                          && !languageDetectionSuggestion
                          && !languageDetectionError
                          && !languageDetectionAttention && (
                          <Alert severity="warning">
                            Detection completed, but no usable suggestion is available. Request a fresh preflight to determine whether the saved result can be restored safely.
                          </Alert>
                        )}

                        {languageDetectionSuggestion && (
                          <Alert severity={languageDetectionSuggestion.mixedLanguage ? 'warning' : 'success'}>
                            <Stack spacing={0.75}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {languageDetectionSuggestion.detectedLanguageCode
                                  ? `Suggested language: ${languageDetectionSuggestion.detectedLanguageCode}`
                                  : 'The detector could not identify one language.'}
                                {languageDetectionSuggestion.confidence != null
                                  ? ` · ${Math.round(languageDetectionSuggestion.confidence * 100)}% confidence`
                                  : ''}
                              </Typography>
                              {languageDetectionSuggestion.projectLocaleCandidates.length > 0 && (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {languageDetectionSuggestion.projectLocaleCandidates.map((locale) => (
                                    <Chip
                                      key={locale}
                                      size="small"
                                      variant="outlined"
                                      label={`Candidate · ${locale}`}
                                      data-testid={PROVIDER_SOURCES.languageDetectionSuggestedLocale(locale)}
                                    />
                                  ))}
                                </Stack>
                              )}
                              <Typography variant="caption">
                                Nothing was selected automatically. Choose a project language below, then confirm it for the exact Meta ad.
                              </Typography>
                            </Stack>
                          </Alert>
                        )}
                      </Stack>
                    </Paper>
                    <Divider />
                    <Typography variant="caption" color="text.secondary">Project languages</Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {projectLocales.map((locale) => (
                        <Chip
                          key={locale.code}
                          clickable
                          color={sourceLocale === locale.code ? 'primary' : 'default'}
                          variant={sourceLocale === locale.code ? 'filled' : 'outlined'}
                          label={`${locale.label} · ${locale.code}`}
                          data-testid={PROVIDER_SOURCES.sourceLocale(locale.code)}
                          onClick={() => {
                            setSourceLocale(locale.code);
                            setTargetLocales((current) => {
                              const next = new Set(current);
                              next.delete(locale.code);
                              return next;
                            });
                            setCreatedDraft(null);
                          }}
                        />
                      ))}
                    </Stack>
                    {!localesLoading && projectLocales.length === 0 && (
                      <Alert severity="warning">Configure project languages before this handoff.</Alert>
                    )}
                    <Button
                      variant={exactLanguageConfirmed ? 'outlined' : 'contained'}
                      startIcon={languageBusy ? <CircularProgress size={14} /> : <LanguageIcon />}
                      disabled={!sourceLocale || !deploymentContext || !baselineReady || baselineBusy || languageBusy || exactLanguageConfirmed}
                      data-testid={PROVIDER_SOURCES.confirmLanguage}
                      onClick={() => void confirmLanguage()}
                    >
                      {exactLanguageConfirmed ? 'Language confirmed for this Meta ad' : 'Confirm language for this Meta ad'}
                    </Button>
                    {detail.languageEvidencePage.hasMore && (
                      <Button
                        size="small"
                        variant="text"
                        disabled={languageEvidenceLoadingMore || baselineBusy || languageBusy || draftBusy}
                        startIcon={languageEvidenceLoadingMore ? <CircularProgress size={13} /> : undefined}
                        onClick={() => void loadMoreLanguageEvidence()}
                      >
                        Load more language evidence
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, opacity: exactLanguageConfirmed ? 1 : 0.65 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{baselineRequired ? '3.' : '2.'} Create a Segmently variation draft</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  The exact snapshot and selected Meta ad binding are inherited. Choose any target languages to add now;
                  content generation and review remain separate actions.
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>Target languages</Typography>
                <Typography variant="caption" color="text.secondary">
                  Optional now. Each selected language is added as missing and review-required; no translation starts.
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  {availableTargetLocales.map((locale) => (
                    <Chip
                      key={locale.code}
                      clickable
                      color={targetLocales.has(locale.code) ? 'primary' : 'default'}
                      variant={targetLocales.has(locale.code) ? 'filled' : 'outlined'}
                      label={`${locale.label} · ${locale.code}`}
                      data-testid={PROVIDER_SOURCES.targetLocale(locale.code)}
                      onClick={() => toggleTargetLocale(locale.code)}
                    />
                  ))}
                  {availableTargetLocales.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No additional project languages are configured.</Typography>
                  )}
                </Stack>

                <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Output: one reviewable Segmently copy</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${sourceLocale || 'Confirm source'}${targetLocales.size ? ` → ${Array.from(targetLocales).join(', ')}` : ''} · exact snapshot · 0 generated assets · 0 Meta changes`}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={draftBusy ? <CircularProgress size={14} /> : <LibraryAddIcon />}
                      disabled={!baselineReady || !exactLanguageConfirmed || deploymentSelectionRequired || baselineBusy || draftBusy || Boolean(createdDraft)}
                      data-testid={PROVIDER_SOURCES.createDraft}
                      onClick={() => void createDraft()}
                    >
                      Create variation draft
                    </Button>
                  </Stack>
                </Box>
              </Paper>

              {createdDraft && (
                <Alert severity="success" data-testid={PROVIDER_SOURCES.createdDraft}>
                  {`Segmently draft ${createdDraft.itemId} ${createdDraft.reused ? 'reused' : 'created'} from exact snapshot ${createdDraft.sourceSnapshotId}. `}
                  {`${createdDraft.generatedAssetCount} assets generated and ${createdDraft.providerMutationCount} Meta changes. `}
                  Return to Create and open Segmently creations to continue manual or AI localization.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {createdDraft && <Button onClick={onBack}>Open Create</Button>}
          <Button
            data-testid={PROVIDER_SOURCES.detailClose}
            onClick={closeDetail}
            disabled={bindingsLoadingMore || languageEvidenceLoadingMore || baselineBusy || languageBusy || draftBusy}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function ProviderBaselineMediaPreview({ choice }: { choice: AdCreativeProviderBaselineChoice }): React.ReactElement | null {
  const visibleMedia = choice.media.filter((media) => Boolean(media.publicUrl));
  if (visibleMedia.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {visibleMedia.map((media, index) => (
        <Box key={`${media.id}:${index}`} sx={{ width: 112 }}>
          {media.kind === 'video' ? (
            <Box
              component="video"
              src={media.publicUrl}
              muted
              playsInline
              preload="metadata"
              data-testid={PROVIDER_SOURCES.baselineMedia(choice.id, index)}
              sx={{ width: 112, height: 84, objectFit: 'cover', borderRadius: 1, bgcolor: 'black' }}
            />
          ) : (
            <Box
              component="img"
              src={media.publicUrl}
              alt={`${choice.label} media ${index + 1}`}
              data-testid={PROVIDER_SOURCES.baselineMedia(choice.id, index)}
              sx={{ width: 112, height: 84, objectFit: 'cover', borderRadius: 1, bgcolor: 'action.hover' }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {media.width && media.height ? `${media.width}×${media.height}` : media.kind}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function mergeProviderSourcePages(
  current: AdCreativeProviderSourceHead[],
  incoming: AdCreativeProviderSourceHead[],
): AdCreativeProviderSourceHead[] {
  return mergeById(current, incoming);
}

function mergeProviderCandidatePages(
  current: AdCreativeProviderSourceCandidate[],
  incoming: AdCreativeProviderSourceCandidate[],
): AdCreativeProviderSourceCandidate[] {
  return mergeById(current, incoming);
}

function mergeById<T extends { id: string }>(current: T[], incoming: T[]): T[] {
  const byId = new Map(current.map((entry) => [entry.id, entry]));
  for (const entry of incoming) byId.set(entry.id, entry);
  return Array.from(byId.values());
}

function providerDetailPageTruth(page: {
  currentSnapshotId: string;
  limit: number;
  hasMore: boolean;
  nextCursor?: string;
}): {
  currentSnapshotId: string;
  limit: number;
  hasMore: boolean;
  nextCursor?: string;
} {
  return {
    currentSnapshotId: page.currentSnapshotId,
    limit: page.limit,
    hasMore: page.hasMore,
    ...(page.nextCursor ? { nextCursor: page.nextCursor } : {}),
  };
}

function languageStateLabel(state: ProviderSourceLanguageUiState): string {
  if (state === 'known') return 'Known source language';
  if (state === 'suggested') return 'Suggested source language';
  return 'Source language unknown';
}

function candidateAssociationLabel(
  state: AdCreativeProviderSourceCandidate['funnelAssociation']['state'],
): string {
  if (state === 'linked_in_window') return 'Linked in this period';
  if (state === 'known_segmently_destination') return 'Known Segmently destination';
  if (state === 'mixed') return 'Mixed destinations';
  if (state === 'not_linked') return 'No association in this period';
  return 'Association unknown';
}

function candidateDestinationHosts(candidate: AdCreativeProviderSourceCandidate): string[] {
  const hosts = candidate.boundedDestinationUrls.flatMap((value) => {
    try {
      return [new URL(value).hostname];
    } catch {
      return [];
    }
  });
  return Array.from(new Set(hosts)).slice(0, 3);
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
